/**
 * Login Form Component - OmniMind style
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { login, error } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showMFA, setShowMFA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    setLoading(true);

    try {
      await login(email, password, mfaCode || undefined);

      if (error === 'MFA_REQUIRED') {
        setShowMFA(true);
        setLoading(false);
        return;
      }

      navigate('/');
    } catch (err: any) {
      setLocalError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app" style={{ display: 'grid', placeItems: 'center', overflowY: 'auto' }}>
      <div
        className="card"
        style={{ width: '100%', maxWidth: 420, padding: '30px 30px 26px', margin: '20px' }}
      >
        {/* Mark and title */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div className="orb" style={{ width: 54, height: 54, borderRadius: 18 }}>
            <i style={{ width: 18, height: 18, boxShadow: '0 0 0 5px rgba(255,255,255,.3)' }} />
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-.04em' }}>
            Sign in to OmniMind
          </h2>
          <p className="dim" style={{ fontSize: 12.5, marginTop: 5 }}>
            The decision layer over every system you run
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {!showMFA ? (
            <>
              <div className="fld">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@company.com"
                />
              </div>

              <div className="fld">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••••••"
                />
              </div>
            </>
          ) : (
            <div className="fld">
              <label htmlFor="mfaCode">Two-factor authentication</label>
              <input
                id="mfaCode"
                type="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                required
                maxLength={6}
                placeholder="000000"
                className="mono"
                style={{ textAlign: 'center', fontSize: 20, letterSpacing: '.35em' }}
              />
              <div className="hint" style={{ textAlign: 'center' }}>
                Enter the 6-digit code from your authenticator app
              </div>
            </div>
          )}

          {(localError || error) && error !== 'MFA_REQUIRED' && (
            <div className="errbox" style={{ margin: '12px 0' }}>
              {localError || error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn btn-p"
            style={{ width: '100%', padding: '11px 14px', marginTop: 6 }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>

          {showMFA && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button
                type="button"
                onClick={() => setShowMFA(false)}
                className="dim"
                style={{ fontSize: 12, fontWeight: 700 }}
              >
                ← Back to login
              </button>
            </div>
          )}
        </form>

        {/* Demo credentials hint */}
        <div className="note" style={{ marginTop: 18, textAlign: 'center' }}>
          <div className="eye" style={{ marginBottom: 3 }}>
            Demo account
          </div>
          <span className="mono" style={{ color: 'var(--ink)', fontSize: 12 }}>
            ceo@demo.omnimind.local
          </span>
        </div>

        <div className="hint" style={{ textAlign: 'center', marginTop: 16 }}>
          By signing in, you agree to our Terms of Service and Privacy Policy
        </div>
      </div>
    </div>
  );
};
